import { EMPTY_SITE_CONFIGURATION } from '../../common/api/defaults';
import { THEME_MODES, THEME_PALETTES } from '../../common/constants';
import type {
  ReferenceChoice,
  SiteConfiguration,
  SitePreferenceRecord,
  SitePreferenceTheme,
  SitePreferenceWritePayload,
  SiteTheme,
  ThemeMode,
  ThemePalette,
} from '../../common/models';

/**
 * The translation layer between step 2's form and the API contract in
 * mdfiles/site-config.md.
 *
 * Two conversions carry it: the four dropdowns hold master ids as strings, and
 * the theme is slugged in the UI ('rose') but labelled on the wire ('Rose').
 */

/* -- theme ----------------------------------------------------------------- */

/** Section 3: the API accepts the enum name or the label; we send the label. */
export function paletteLabel(palette: ThemePalette): string {
  return THEME_PALETTES.find((entry) => entry.id === palette)?.label ?? 'Default';
}

export function modeLabel(mode: ThemeMode): string {
  return THEME_MODES.find((entry) => entry.id === mode)?.label ?? 'Light';
}

/**
 * Wire value -> the UI's slug. Accepts a label ("Rose"), an enum name, or any
 * casing, and falls back to the section 3 default rather than to nothing --
 * an unrecognised palette must not leave the swatch grid with no selection.
 */
export function paletteFromApi(value: string | null | undefined): ThemePalette {
  const needle = value?.trim().toLowerCase();
  if (!needle) return 'default';
  return (
    THEME_PALETTES.find(
      (entry) => entry.id === needle || entry.label.toLowerCase() === needle,
    )?.id ?? 'default'
  );
}

export function modeFromApi(value: string | null | undefined): ThemeMode {
  const needle = value?.trim().toLowerCase();
  if (!needle) return 'light';
  return (
    THEME_MODES.find((entry) => entry.id === needle || entry.label.toLowerCase() === needle)
      ?.id ?? 'light'
  );
}

function themeFromApi(theme: SitePreferenceTheme | null | undefined): SiteTheme {
  return { palette: paletteFromApi(theme?.palette), mode: modeFromApi(theme?.mode) };
}

/* -- reference defaults ---------------------------------------------------- */

/**
 * Section 4: the row flagged `isDefault` is what a fresh step 2 preselects.
 * Empty string when the list has no default, which leaves the dropdown on its
 * placeholder rather than silently picking the first row.
 */
export function defaultChoiceValue(choices: readonly ReferenceChoice[]): string {
  return choices.find((entry) => entry.isDefault)?.value ?? '';
}

/* -- record -> form -------------------------------------------------------- */

function idToValue(id: number | null | undefined): string {
  return typeof id === 'number' ? String(id) : '';
}

/**
 * The name to show for an image the server gave no name for.
 *
 * A floor rather than the usual path: `osp_login_image_name` (V20261002091000)
 * carries the picked name, and the migration backfilled the rows that predate it
 * with exactly what this returns.
 */
export function loginImageNameFromDataUrl(dataUrl: string | null | undefined): string {
  if (!dataUrl) return '';
  return dataUrl.startsWith('data:image/jpeg') ? 'login-image.jpg' : 'login-image.png';
}

/** GET/POST/PUT response -> the form's values (section 5.4). */
export function recordToSiteConfiguration(record: SitePreferenceRecord): SiteConfiguration {
  return {
    ...EMPTY_SITE_CONFIGURATION,
    currency: idToValue(record.currencyId),
    dateFormat: idToValue(record.dateFormatId),
    timeFormat: idToValue(record.timeFormatId),
    timeZone: idToValue(record.timeZoneId),
    theme: themeFromApi(record.configurations?.theme),
    /* The stored name first; the synthesized one only where there is no stored name. */
    loginImageName: record.loginImageName ?? loginImageNameFromDataUrl(record.loginImage),
    loginImageDataUrl: record.loginImage ?? '',
  };
}


/** The country's currency when the list carries it, and the list's own default otherwise. */
function currencyFor(
  currencies: readonly ReferenceChoice[],
  countryCurrencyId: number | null | undefined,
): string {
  if (countryCurrencyId !== null && countryCurrencyId !== undefined) {
    const named = currencies.find((choice) => choice.value === String(countryCurrencyId));
    if (named) return named.value;
  }
  return defaultChoiceValue(currencies);
}

/**
 * What step 2 shows when the site-preference GET returns 404 -- expected, not
 * an error: no ids selected beyond each list's own default, and the section 3
 * theme defaults.
 */
export function defaultSiteConfiguration(
  options: {
    currencies: readonly ReferenceChoice[];
    dateFormats: readonly ReferenceChoice[];
    timeFormats: readonly ReferenceChoice[];
    timeZones: readonly ReferenceChoice[];
  },
  /**
   * The currency the organization's country is denominated in, where it names one.
   *
   * <p>Asked for: the currency should follow the country somebody has already chosen, rather than
   * whichever currency happens to sort first. Only a default — the control stays editable, and a country
   * that names no currency, or one the list does not carry, falls back to the list's own default rather
   * than leaving the field empty.
   */
  countryCurrencyId?: number | null,
): SiteConfiguration {
  return {
    ...EMPTY_SITE_CONFIGURATION,
    currency: currencyFor(options.currencies, countryCurrencyId),
    dateFormat: defaultChoiceValue(options.dateFormats),
    timeFormat: defaultChoiceValue(options.timeFormats),
    timeZone: defaultChoiceValue(options.timeZones),
    theme: { palette: 'default', mode: 'light' },
  };
}

/* -- form -> payload ------------------------------------------------------- */

function toId(value: string): number | null {
  const parsed = Number(value);
  return value.trim() !== '' && Number.isFinite(parsed) ? parsed : null;
}

function orNull(value: string): string | null {
  const trimmed = value.trim();
  return trimmed === '' ? null : trimmed;
}

/**
 * The form's values -> the POST/PUT body (sections 5.1, 5.2 -- one shape for
 * both).
 *
 * Every field is always present, because the update is a full replacement.
 * `organizationId` and `legalEntityId` are never included: the first comes
 * from the path, the second is always null for this wizard. The five reserved
 * fields the API returns as null (`description`, `fontType`, `fontSize`,
 * `colorPrimary`, `colorSecondary`) are omitted for the same reason.
 */
export function siteConfigurationToPayload(
  values: SiteConfiguration,
): SitePreferenceWritePayload {
  return {
    currencyId: toId(values.currency),
    dateFormatId: toId(values.dateFormat),
    timeFormatId: toId(values.timeFormat),
    timeZoneId: toId(values.timeZone),
    theme: {
      palette: paletteLabel(values.theme.palette),
      mode: modeLabel(values.theme.mode),
    },
    loginImage: orNull(values.loginImageDataUrl ?? ''),
    /* The picked name, so the chip still reads it after a save and a reload. */
    loginImageName: orNull(values.loginImageName),
  };
}
